/**
 * F16.F.2: dedicated Skills surface inside a portal. Lists every Skill
 * synced under this portal's slug (F16.E `listForSlug`), shows the
 * selected Skill's frontmatter + body, and exposes a version history
 * drawer driven by `skills.getWithVersions`.
 *
 * Scope-narrow on purpose: F16.F.2 is a read surface. Authoring stays
 * in SharePoint; F16.G wires save-back; F12 wires webhook-driven sync.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { useTenantBranding } from '../../../lib/tenant/TenantBrandingContext';
import { useSkillSync } from '../../../hooks/useSkillSync';
import { testIds } from '../../../lib/testIds';
import type { Id } from '../../../../convex/_generated/dataModel';

interface DisplaySkill {
  _id: string;
  name: string;
  description: string;
  currentVersion: number;
}

function PortalSkillsRoute() {
  const { branding, slug } = useTenantBranding();
  const { status: syncStatus, sync } = useSkillSync();

  const liveSkills = useQuery(api.skills.listForSlug, { clientSlug: slug });
  const skills: DisplaySkill[] | undefined = liveSkills?.map((s) => ({
    _id: String(s._id),
    name: s.name,
    description: s.description,
    currentVersion: s.currentVersion,
  }));

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const detail = useQuery(
    api.skills.getWithVersions,
    selectedId ? { skillId: selectedId as Id<'skills'> } : 'skip',
  );
  const sortedVersions = detail
    ? [...detail.versions].sort((a, b) => a.versionNumber - b.versionNumber)
    : [];
  const latestVersion = sortedVersions[sortedVersions.length - 1] ?? null;

  return (
    <section
      data-testid={testIds.portal.skillsRoot}
      className="max-w-6xl mx-auto px-6 py-6 flex flex-col gap-4 text-black"
    >
      <header className="flex items-center gap-3">
        <h2 className="text-2xl font-semibold tracking-tight flex-1">
          Skills · {branding.displayName}
        </h2>
        <button
          type="button"
          data-testid={testIds.portal.skillsSyncButton}
          onClick={() => void sync()}
          disabled={syncStatus.kind === 'syncing'}
          className="text-sm px-3 py-1 border border-black hover:bg-black hover:text-white disabled:opacity-50"
        >
          {syncStatus.kind === 'syncing' ? 'Syncing…' : 'Sync from SharePoint'}
        </button>
      </header>

      {syncStatus.kind === 'error' && (
        <div
          role="alert"
          data-testid={testIds.portal.skillsSyncError}
          className="text-sm border border-red-700 bg-red-50 text-red-900 px-3 py-2"
        >
          {syncStatus.message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 border border-black/40">
        <aside
          data-testid={testIds.portal.skillsListPane}
          className="border-r border-black/40 max-h-[60vh] overflow-auto"
        >
          {skills === undefined && (
            <p className="px-3 py-4 text-sm text-gray-700">Loading Skills…</p>
          )}
          {skills && skills.length === 0 && (
            <p
              data-testid={testIds.portal.skillsEmpty}
              className="px-3 py-4 text-sm text-gray-700"
            >
              No Skills synced for this portal yet. Click{' '}
              <strong>Sync from SharePoint</strong> if a Firm-Skills site is
              configured, or run the operator CLI to add one.
            </p>
          )}
          {skills && skills.length > 0 && (
            <ul className="divide-y divide-black/20">
              {skills.map((s) => (
                <li key={s._id}>
                  <button
                    type="button"
                    data-testid={testIds.portal.skillsListRow(s._id)}
                    onClick={() => {
                      setSelectedId(s._id);
                      setHistoryOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-black/[0.05] ${
                      selectedId === s._id ? 'bg-black/[0.08]' : ''
                    }`}
                  >
                    <div className="font-mono text-xs uppercase tracking-wide">
                      {s.name}
                    </div>
                    <div className="text-xs text-gray-700 mt-0.5 line-clamp-2">
                      {s.description}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <article
          data-testid={testIds.portal.skillsDetailPane}
          className="p-4 overflow-auto max-h-[60vh]"
        >
          {selectedId === null ? (
            <p className="text-sm text-gray-700">
              Select a Skill on the left to preview its prompt body.
            </p>
          ) : detail === undefined ? (
            <p className="text-sm text-gray-700">Loading Skill…</p>
          ) : detail === null ? (
            <p className="text-sm text-gray-700">
              Skill not found or no access.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-semibold flex-1">
                  {detail.skill.name}
                </h3>
                <span
                  data-testid={testIds.portal.skillsVersionPill}
                  className="text-xs font-mono px-2 py-0.5 border border-black"
                >
                  v{detail.skill.currentVersion}
                </span>
                <button
                  type="button"
                  data-testid={testIds.portal.skillsHistoryButton}
                  onClick={() => setHistoryOpen(true)}
                  className="text-sm px-2 py-1 border border-black hover:bg-black hover:text-white"
                >
                  View history
                </button>
              </div>
              <p className="text-sm">{detail.skill.description}</p>
              <pre
                data-testid={testIds.portal.skillsBodyPreview}
                className="text-xs whitespace-pre-wrap border border-black/30 p-3 bg-black/[0.02] max-h-[40vh] overflow-auto font-mono"
              >
                {latestVersion?.body ?? '(no body)'}
              </pre>
            </div>
          )}
        </article>
      </div>

      {historyOpen && detail && (
        <aside
          role="dialog"
          aria-modal="true"
          data-testid={testIds.portal.skillsHistoryDrawer}
          className="fixed inset-y-0 right-0 z-[900] w-[420px] bg-white border-l border-black shadow-xl overflow-auto"
        >
          <header className="flex items-center justify-between px-4 py-2 border-b border-black bg-black/[0.04]">
            <h3 className="text-sm font-semibold">
              History · {detail.skill.name}
            </h3>
            <button
              type="button"
              data-testid={testIds.portal.skillsHistoryClose}
              onClick={() => setHistoryOpen(false)}
              className="text-sm px-2 py-1 border border-black hover:bg-black hover:text-white"
              aria-label="Close history"
            >
              ✕
            </button>
          </header>
          <ol className="px-4 py-3 flex flex-col gap-3">
            {sortedVersions.map((v) => (
              <li
                key={v.versionNumber}
                data-testid={testIds.portal.skillsHistoryVersion(
                  v.versionNumber,
                )}
                className="border border-black/30 p-3"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs px-2 py-0.5 border border-black">
                    v{v.versionNumber}
                  </span>
                  <span className="text-xs text-gray-700">
                    {new Date(v.createdAt).toLocaleString()}
                  </span>
                </div>
                <pre className="mt-2 text-[11px] whitespace-pre-wrap font-mono max-h-40 overflow-auto bg-black/[0.02] p-2 border border-black/20">
                  {v.body}
                </pre>
              </li>
            ))}
          </ol>
        </aside>
      )}
    </section>
  );
}

export const Route = createFileRoute('/c/$clientSlug/skills')({
  component: PortalSkillsRoute,
});
